import React from 'react';
import { Card, Badge } from 'ui';
import { FormatCurrency } from 'utils/features/formatCurrency';
import { motion } from 'framer-motion';
import { useStackAnimation } from 'hooks/useStackAnimation';

interface ISummaryItem {
	label: string;
	amount: number;
}

interface ISummaryCardProps {
	items: ISummaryItem[];
	isDataReady: boolean;
}

export const SummaryCard: React.FC<ISummaryCardProps> = ({
	items,
	isDataReady,
}) => {
	const getGridClass = (length: number) => {
		switch (length) {
			case 1:
				return 'grid-cols-1';
			case 2:
				return 'grid-cols-1 md:grid-cols-2';
			case 3:
				return 'grid-cols-1 md:grid-cols-3';
			case 4:
				return 'grid-cols-2 md:grid-cols-4 xs-sm:grid-cols-1';
			default:
				return 'grid-cols-2 md:grid-cols-4';
		}
	};

	const lengthOfItems = items.length;
	const currencyClass =
		lengthOfItems > 3
			? 'text-4xl lg:text-4xl xl:text-5xl'
			: 'text-4xl md-lg:text-4xl lg:text-5xl xl:text-6xl';

	const motionProps = useStackAnimation(0.3, isDataReady);

	return (
		<Card border="2xl" className="w-full mb-5 p-5 !rounded-3xl bg-background ">
			<motion.div
				initial={{ opacity: 0, y: 20 }}
				animate={{ opacity: 1, y: 0 }}
				transition={{ duration: 0.4 }}
			>
				<Card
					border="2xl"
					className="bg-background border-[1.5px] border-grayColor rounded-3xl shadow-lg"
				>
					<div className={`grid ${getGridClass(items.length)}`}>
						{items.map((item, index) => {
							const isLast = index === items.length - 1;
							const isEvenInGrid = index % 2 === 0;
							const showVerticalDivider = !isLast && items.length > 1;
							const showHorizontalDivider =
								(items.length === 3 && index < 2) ||
								(items.length === 4 && index < 2);

							return (
								<motion.div
									key={item.label}
									initial={motionProps.initial}
									animate={motionProps.animate}
									custom={index}
									className={`
									relative
									p-6
									flex
									flex-col
									justify-between
									min-h-[140px]

									xs-sm:items-center

									md:p-4

									lg:p-6
									lg:pl-5
									lg:min-h-[160px]
									
									${showVerticalDivider ? 'md:border-r md:border-grayColor' : ''}
									${showHorizontalDivider ? 'border-b border-grayColor md:border-b-0' : ''}
									${items.length === 4 && !isEvenInGrid ? 'border-l border-grayColor' : ''}
								`}
								>
									<Badge
										size="xs"
										variant="secondary"
										className="mb-2 bg-primary-badge text-primary-badge-foreground w-fit whitespace-nowrap"
									>
										{item.label}
									</Badge>
									<div className="flex">
										<FormatCurrency
											value={item.amount}
											className={`font-dm-sans font-light ${currencyClass}`}
										/>
									</div>
								</motion.div>
							);
						})}
					</div>
				</Card>
			</motion.div>
		</Card>
	);
};
